import { HtmlService } from './HtmlService';

/**
 * UserTextService
 *
 * This service class provides support for "user text blocks". These are
 * text blocks (html or plain) input by the user.
 */

class UserTextServiceCls {
  /**
   * Replace various standard placeholders with activity fields.
   */
  replaceStdPlaceholders(activity, text, dfltPlaceholder = '-') {
    if (!activity) {
      return '';
    }
    if (!text) {
      return '';
    }

    return text
      .replaceAll(
        /\[userName\]/gi,
        HtmlService.sanitiseHTML(activity.owner?.name || dfltPlaceholder)
      )
      .replace(
        /\[interviewerName\]/gi,
        HtmlService.sanitiseHTML(activity.owner?.name || dfltPlaceholder)
      )
      .replace(
        /\[activityName\]/gi,
        HtmlService.sanitiseHTML(activity.name || dfltPlaceholder)
      )
      .replace(
        /\[organisationName\]/gi,
        HtmlService.sanitiseHTML(
          activity.organisation?.name ||
            activity.owner?.organisation?.name ||
            dfltPlaceholder
        )
      );
  }

  /**
   * Convert the generic placeholders (e.g. +++text***) to editable HTML <span>s
   */
  convertGenericPlaceholdersToEditableHtml(text) {
    if (!text) {
      return '';
    }

    return text
      .replaceAll(
        '+++',
        '<span class="content-highlight" contenteditable="true">'
      )
      .replaceAll('***', '</span>');
  }

  /**
   * Get all placeholder field values. Search for both generic placeholder fields
   * and html content-editable fields
   *      +++blah***
   *      <span class="content-highlight" contenteditable="true">Blah</span>
   */
  getAllPlaceholderFieldValues(text) {
    if (!text) {
      return { matchInfo: [] };
    }

    // try generic placeholders first
    let matchInfo = [...text.matchAll(/\+\+\+(.*?)\*\*\*/g)];
    if (matchInfo.length) {
      return { type: 'generic', matchInfo };
    }

    // try html content-editable spans
    matchInfo = [
      ...text.matchAll(
        /<span class="content-highlight" contenteditable="true">(.*?)<\/span>/g
      ),
    ];
    return { type: 'html', matchInfo };
  }

  /**
   * Replace all placeholders in the given text according to the match info, as
   * returned by getAllPlaceholderFieldValues().
   */
  substituteAllPlaceholders(text, fromMatches, toMatches) {
    if (!text) {
      return '';
    }
    if (!fromMatches?.matchInfo || !toMatches?.matchInfo) {
      return text || '';
    }
    if (fromMatches.matchInfo.length !== toMatches.matchInfo.length) {
      return text || '';
    }

    for (let i = 0; i < fromMatches.matchInfo.length; i++) {
      text = text.replaceAll(
        fromMatches.matchInfo[i][0],
        toMatches.matchInfo[i][0]
      );
    }

    return text;
  }

  /**
   * Bring across all placeholder text in srcText to targetText. There must
   * be a 1-1 alignment of placeholders in both text blocks.
   */
  transferAllPlaceholders(srcText, targetText) {
    const bail = { updated: false };

    if (!srcText || !targetText) {
      return bail;
    }

    const srcMatches = this.getAllPlaceholderFieldValues(srcText);
    const targetMatches = this.getAllPlaceholderFieldValues(targetText);

    if (!srcMatches.matchInfo || !targetMatches.matchInfo) {
      return bail;
    }
    if (srcMatches.matchInfo.length !== targetMatches.matchInfo.length) {
      return bail;
    }

    return {
      updated: true,
      text: this.substituteAllPlaceholders(
        targetText,
        targetMatches,
        srcMatches
      ),
    };
  }

  /**
   * Convert editable <span>s back to vanilla <span>s
   */
  removeEditableText(html) {
    if (!html) {
      return '';
    }

    return html.replaceAll(
      '<span class="content-highlight" contenteditable="true">',
      '<span>'
    );
  }
}

// service class is a singleton
const UserTextService = new UserTextServiceCls();

export { UserTextService };
